import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserPlus, faChartBar, faArchive, faEdit, faTrash, faCircleCheck, faPause, faStop, faCircleXmark, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const SupervisorDashboard = () => {
    const [interviewers, setInterviewers] = useState([]);
    const [archivedCount, setArchivedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        suspended: 0,
        inactive: 0,
        verified: 0,
        unverified: 0
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [interviewersRes, archivedRes] = await Promise.all([
                api.get('/interviewers'),
                api.get('/interviewers/archived')
            ]);
            
            // Handle new API response format
            const interviewersData = interviewersRes.data.success ? interviewersRes.data.data : [];
            const archivedData = archivedRes.data.success ? archivedRes.data.data : [];
            
            setInterviewers(interviewersData);
            setArchivedCount(archivedData.length);
            
            // Calculate stats
            const total = interviewersData.length;
            const active = interviewersData.filter(i => i.status === 'active').length;
            const suspended = interviewersData.filter(i => i.status === 'suspended').length;
            const inactive = interviewersData.filter(i => i.status === 'inactive').length;
            const verified = interviewersData.filter(i => i.verified === 'yes').length;
            const unverified = interviewersData.filter(i => i.verified === 'no').length;
            
            setStats({ total, active, suspended, inactive, verified, unverified });
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return faCircleCheck;
            case 'suspended': return faPause;
            case 'inactive': return faStop;
            default: return faCircleCheck;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'text-green-600';
            case 'suspended': return 'text-red-600';
            case 'inactive': return 'text-yellow-600';
            default: return 'text-green-600';
        }
    };

    const getVerifiedIcon = (verified) => {
        switch (verified) {
            case 'yes': return faCircleCheck;
            case 'partially': return faCircleExclamation;
            case 'no': return faCircleXmark;
            default: return faCircleXmark;
        }
    };

    const getVerifiedColor = (verified) => {
        switch (verified) {
            case 'yes': return 'text-green-600';
            case 'partially': return 'text-yellow-600';
            case 'no': return 'text-gray-500';
            default: return 'text-gray-500';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">Tableau de Bord Prestataire</h1>
                        <p className="text-gray-600">Gérez vos enquêteurs et suivez les performances</p>
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Stats Overview - Large Card */}
                        <div className="lg:col-span-8 bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Vue d'ensemble</h2>
                                <FontAwesomeIcon icon={faChartBar} className="text-3xl text-red-600" />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                                    <div className="text-sm text-gray-600">Total Enquêteurs</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600">{stats.active}</div>
                                    <div className="text-sm text-gray-600">Actifs</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-red-600">{stats.suspended}</div>
                                    <div className="text-sm text-gray-600">Suspendus</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-yellow-600">{stats.inactive}</div>
                                    <div className="text-sm text-gray-600">Inactifs</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600">{stats.verified}</div>
                                    <div className="text-sm text-gray-600">Vérifiés</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-gray-500">{stats.unverified}</div>
                                    <div className="text-sm text-gray-600">Non Vérifiés</div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="lg:col-span-4 bg-gradient-to-br from-red-50 to-red-100 rounded-3xl p-6 shadow-xl border border-red-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Actions Rapides</h3>
                            <div className="space-y-3">
                                <Link
                                    to="/interviewers/create"
                                    className="flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="text-red-600 mr-3" />
                                    <span className="font-medium">Nouvel Enquêteur</span>
                                </Link>
                                <Link
                                    to="/interviewers"
                                    className="flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faUsers} className="text-red-600 mr-3" />
                                    <span className="font-medium">Gérer les Enquêteurs</span>
                                </Link>
                                <Link
                                    to="/interviewers/archived"
                                    className="flex items-center p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faArchive} className="text-red-600 mr-3" />
                                    <span className="font-medium">Voir les Archivés ({archivedCount})</span>
                                </Link>
                            </div>
                        </div>

                        {/* Recent Interviewers */}
                        <div className="lg:col-span-8 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl border border-gray-100">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-0">Enquêteurs Récents</h3>
                                <Link
                                    to="/interviewers"
                                    className="text-red-600 hover:text-red-700 font-medium text-sm sm:text-base"
                                >
                                    Voir tout
                                </Link>
                            </div>
                            {/* Column Headers */}
                            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-3 py-2 bg-gray-100 rounded-lg mb-3">
                                <div className="col-span-6">
                                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wider">Enquêteur</span>
                                </div>
                                <div className="col-span-3 text-center">
                                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wider">Statut</span>
                                </div>
                                <div className="col-span-3 text-center">
                                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wider">Vérifié</span>
                                </div>
                            </div>

                            <div className="space-y-2 sm:space-y-1">
                                {interviewers.slice(0, 5).map((interviewer) => (
                                    <div key={interviewer.id} className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                                        {/* Mobile Layout */}
                                        <div className="sm:hidden">
                                            <div className="flex items-center space-x-3 mb-3">
                                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-red-600 font-bold text-xs">
                                                        {interviewer.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium text-gray-900 text-sm truncate">{interviewer.name}</div>
                                                    <div className="text-xs text-gray-600 truncate">{interviewer.email}</div>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center gap-4">
                                                <div className="flex items-center space-x-1">
                                                    <span className="text-xs text-gray-500 font-medium">Statut:</span>
                                                    <div className="flex items-center space-x-1">
                                                        <FontAwesomeIcon 
                                                            icon={getStatusIcon(interviewer.status)} 
                                                            className={`text-xs ${getStatusColor(interviewer.status)}`}
                                                        />
                                                        <span className="text-xs text-gray-600 capitalize">{interviewer.status}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    <span className="text-xs text-gray-500 font-medium">Vérifié:</span>
                                                    <div className="flex items-center space-x-1">
                                                        <FontAwesomeIcon 
                                                            icon={getVerifiedIcon(interviewer.verified)} 
                                                            className={`text-xs ${getVerifiedColor(interviewer.verified)}`}
                                                        />
                                                        <span className="text-xs text-gray-600 capitalize">
                                                            {interviewer.verified === 'yes' ? 'Oui' : 
                                                             interviewer.verified === 'partially' ? 'Partiellement' : 'Non'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Desktop Layout */}
                                        <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center">
                                            {/* Interviewer Info */}
                                            <div className="col-span-6 flex items-center space-x-4">
                                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-red-600 font-bold text-sm">
                                                        {interviewer.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium text-gray-900 text-base truncate">{interviewer.name}</div>
                                                    <div className="text-sm text-gray-600 truncate">{interviewer.email}</div>
                                                </div>
                                            </div>
                                            
                                            {/* Status */}
                                            <div className="col-span-3 flex items-center justify-center space-x-1">
                                                <FontAwesomeIcon 
                                                    icon={getStatusIcon(interviewer.status)} 
                                                    className={`text-sm ${getStatusColor(interviewer.status)}`}
                                                />
                                                <span className="text-sm text-gray-600 capitalize">{interviewer.status}</span>
                                            </div>
                                            
                                            {/* Verification */}
                                            <div className="col-span-3 flex items-center justify-center space-x-1">
                                                <FontAwesomeIcon 
                                                    icon={getVerifiedIcon(interviewer.verified)} 
                                                    className={`text-sm ${getVerifiedColor(interviewer.verified)}`}
                                                />
                                                <span className="text-sm text-gray-600 capitalize">
                                                    {interviewer.verified === 'yes' ? 'Oui' : 
                                                     interviewer.verified === 'partially' ? 'Partiellement' : 'Non'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {interviewers.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        Aucun enquêteur trouvé
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status Distribution */}
                        <div className="lg:col-span-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 shadow-xl border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Répartition des Statuts</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Actifs</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{stats.active}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Suspendus</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{stats.suspended}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                        <span className="text-sm text-gray-600">Inactifs</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{stats.inactive}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupervisorDashboard;
